import { useState, useRef, useCallback, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import isEmpty from 'utility/Check/isEmpty';
import { convertUrlToFilter } from 'utility/Convert/Search/urlToFilter';
import { CommunicationContext } from 'utility/Provide/Context/PageContext';
import { trackGa4Event } from 'utility/Track/trackingHelper';

import { KiFilterResponse_KiStatus } from 'proto/Api/Llm/KiFilter';
import { ActiveFilter } from 'proto/Search/ActiveFilter';
import { RequestFilterGroup } from 'proto/Search/KiFilter/RequestFilterGroup';

import { getFilters } from '../__helper/getFilters';
import { EVENT_REMOVE_ALL_KI_FILTER } from '../config';
import { SearchState, ErrorMessage } from '../types';

export const useApplyFilter = (
    inputValue: string,
    currentUrl: string,
    filterData: () => RequestFilterGroup[],
    updateFilter: (filter: ReturnType<typeof convertUrlToFilter>) => void
) => {
    const { t } = useTranslation();
    const { react } = useContext(CommunicationContext);

    const [searchState, setSearchState] = useState<SearchState>(
        SearchState.Inactive
    );
    const [errorMessage, setErrorMessage] = useState<ErrorMessage | null>(null);
    const [filters, setFilters] = useState<ActiveFilter[]>([]);
    const abortControllerRef = useRef<AbortController | undefined>();

    const handleApplyFilter = useCallback(async () => {
        if (searchState !== SearchState.Inactive) {
            return;
        }

        if (isEmpty(inputValue)) {
            setErrorMessage({
                title: t('TRANS-try-with-terms-like-all-inclusive-or-pool'),
            });

            setSearchState(SearchState.Inactive);

            return;
        }

        trackGa4Event('kiFilter_click');

        setSearchState(SearchState.Active);
        abortControllerRef.current?.abort();
        abortControllerRef.current = new AbortController();

        const response = await getFilters(
            inputValue,
            currentUrl,
            filterData(),
            abortControllerRef.current
        );

        if (!response) {
            setSearchState(SearchState.Inactive);
            return;
        }

        setSearchState(SearchState.Inactive);
        switch (response.kiStatus) {
            case KiFilterResponse_KiStatus.Ok:
                setErrorMessage(null);
                updateFilter(
                    convertUrlToFilter(decodeURIComponent(response.portalUrl))
                );
                setFilters(response.activeFilterList ?? []);
                break;

            case KiFilterResponse_KiStatus.NoResult:
                setErrorMessage({
                    title: t('TRANS-no-suitable-filters-found'),
                    hint: t('TRANS-try-again-with-new-formulation'),
                });
                setFilters([]);
                break;

            case KiFilterResponse_KiStatus.Timeout:
                setErrorMessage({
                    title: t('TRANS-network-connection-interrupted'),
                    hint: t('TRANS-try-again-at-later-time'),
                });
                setFilters([]);
                break;

            case KiFilterResponse_KiStatus.PartialSuccess:
                setErrorMessage({
                    title: t('TRANS-filter-partially-not-set'),
                });
                updateFilter(
                    convertUrlToFilter(decodeURIComponent(response.portalUrl))
                );
                setFilters(response.activeFilterList ?? []);
                break;

            case KiFilterResponse_KiStatus.Error:
            default:
                setErrorMessage({
                    title: t('TRANS-no-suitable-filters-found'),
                    hint: t('TRANS-try-again-with-new-formulation'),
                });
                setFilters([]);
                break;
        }
    }, [t, inputValue, currentUrl, filterData, updateFilter, searchState]);

    const handleCancelRequest = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            setSearchState(SearchState.Inactive);
        }
    }, []);

    useEffect(() => {
        const clearError = () => setErrorMessage(null);
        react.bind(EVENT_REMOVE_ALL_KI_FILTER, clearError);
        return () => {
            react.unbind(EVENT_REMOVE_ALL_KI_FILTER, clearError);
        };
    }, [react]);

    return {
        handleApplyFilter,
        handleCancelRequest,
        searchState,
        errorMessage,
        filters,
    };
};
